import { ALERT_STATUSES, SEVERITIES, type Alert } from '@playplate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EmptyState, ErrorState, Loading, PageHeader, Panel, SeverityBadge } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError, query } from '../lib/api';
import { formatDateTime, formatRelative, humanEventType } from '../lib/format';

export function AlertsPage() {
  const [status, setStatus] = useState<string>('');
  const [severity, setSeverity] = useState<string>('');

  const alertsQuery = useQuery({
    queryKey: ['alerts', 'list', status, severity],
    queryFn: () =>
      api.get<{ items: Alert[] }>(
        `/alerts${query({ status: status || undefined, severity: severity || undefined, limit: 100 })}`,
      ),
  });

  return (
    <div>
      <PageHeader
        title="Alerts"
        subtitle="One alert per situation. Repeats increment the count rather than creating a new row."
      />

      <Panel className="mb-4">
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="label mb-1.5 block">Status</span>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              {ALERT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label mb-1.5 block">Severity</span>
            <select className="input" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">All</option>
              {SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Panel>

      {alertsQuery.isLoading ? (
        <Loading label="Loading alerts" />
      ) : alertsQuery.isError ? (
        <ErrorState message={(alertsQuery.error as Error).message} onRetry={() => void alertsQuery.refetch()} />
      ) : alertsQuery.data!.items.length === 0 ? (
        <EmptyState title="No alerts" message="Nothing has met an alert rule in this filter." />
      ) : (
        <Panel>
          <ul className="divide-y divide-line/50">
            {alertsQuery.data!.items.map((alert) => (
              <li key={alert.id}>
                <Link to={`/alerts/${alert.id}`} className="block px-4 py-3 transition-colors hover:bg-panel-raised">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold">{alert.title}</p>
                      <p className="mt-0.5 text-xs text-muted">
                        {formatDateTime(alert.firstSeenAt)}
                        {alert.occurrenceCount > 1
                          ? ` · ${alert.occurrenceCount} occurrences, last ${formatRelative(alert.lastSeenAt)}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusChip status={alert.status} />
                      <SeverityBadge severity={alert.severity} />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

type Delivery = {
  channel: string;
  status: string;
  recipient: string;
  attempt: number;
  errorMessage: string | null;
  skippedReason: string | null;
  sentAt: string | null;
  createdAt: string;
};

export function AlertDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const alertQuery = useQuery({
    queryKey: ['alerts', 'detail', id],
    queryFn: () => api.get<Alert & { deliveries: Delivery[] }>(`/alerts/${id}`),
    enabled: Boolean(id),
  });

  const acknowledge = useMutation({
    mutationFn: () => api.post(`/alerts/${id}/acknowledge`, { note: note || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setNote('');
      setActionError(null);
    },
    onError: (e) => setActionError(e instanceof ApiError ? e.message : 'Could not acknowledge'),
  });

  const resolve = useMutation({
    mutationFn: () => api.post(`/alerts/${id}/resolve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setActionError(null);
    },
    onError: (e) => setActionError(e instanceof ApiError ? e.message : 'Could not resolve'),
  });

  if (alertQuery.isLoading) return <Loading label="Loading alert" />;
  if (alertQuery.isError) {
    return <ErrorState message={(alertQuery.error as Error).message} onRetry={() => void alertQuery.refetch()} />;
  }

  const alert = alertQuery.data!;
  const open = alert.status === 'NEW' || alert.status === 'ACKNOWLEDGED';

  return (
    <div>
      <button type="button" className="btn-ghost mb-4 -ml-2" onClick={() => navigate('/alerts')}>
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to alerts
      </button>

      <PageHeader
        title={alert.title}
        subtitle={`${humanEventType(alert.eventType)} · first seen ${formatDateTime(alert.firstSeenAt)}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusChip status={alert.status} />
            <SeverityBadge severity={alert.severity} count={alert.occurrenceCount} />
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Panel title="What happened">
            <div className="space-y-3 p-4">
              <p className="text-sm">{alert.body}</p>
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                <Detail label="Camera" value={alert.cameraName ?? 'System'} />
                <Detail label="Zone" value={alert.zoneName ?? '—'} />
                <Detail label="Occurrences" value={String(alert.occurrenceCount)} />
                <Detail label="First seen" value={formatDateTime(alert.firstSeenAt)} />
                <Detail label="Last seen" value={formatDateTime(alert.lastSeenAt)} />
                {alert.acknowledgedAt ? (
                  <Detail
                    label="Acknowledged"
                    value={`${formatDateTime(alert.acknowledgedAt)}${
                      alert.acknowledgedByName ? ` by ${alert.acknowledgedByName}` : ''
                    }`}
                  />
                ) : null}
              </dl>

              {alert.snapshotPath ? (
                <figure className="mt-2">
                  <img
                    src={`/api/snapshots/${encodeURIComponent(alert.snapshotPath)}`}
                    alt={`Snapshot from ${alert.cameraName ?? 'camera'} at the time of the alert`}
                    className="max-h-96 rounded-lg border border-line"
                  />
                  <figcaption className="mt-1.5 text-xs text-muted">
                    Snapshot taken at the time of the alert. Automatically deleted after the retention
                    period.
                  </figcaption>
                </figure>
              ) : null}
            </div>
          </Panel>

          <Panel title="Delivery log">
            {alert.deliveries.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted">
                No outbound notifications were attempted for this alert.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr>
                      <th className="table-header">Channel</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Recipient</th>
                      <th className="table-header">When</th>
                      <th className="table-header">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alert.deliveries.map((delivery, index) => (
                      <tr key={`${delivery.channel}-${index}`}>
                        <td className="table-cell font-medium">{delivery.channel}</td>
                        <td className="table-cell">
                          <DeliveryChip status={delivery.status} />
                        </td>
                        <td className="table-cell font-mono text-xs text-muted">{delivery.recipient}</td>
                        <td className="table-cell text-xs text-muted">
                          {formatDateTime(delivery.sentAt ?? delivery.createdAt)}
                        </td>
                        <td className="table-cell text-xs text-muted">
                          {delivery.skippedReason ?? delivery.errorMessage ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Respond">
            <div className="space-y-3 p-4">
              {!open ? (
                <p className="text-sm text-muted">
                  This alert is {alert.status.toLowerCase()}. No further action is needed.
                </p>
              ) : (
                <>
                  {alert.status === 'NEW' && can('alert:ack') ? (
                    <>
                      <label className="block">
                        <span className="label mb-1.5 block">Note (optional)</span>
                        <textarea
                          className="input min-h-[80px]"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="What did you find?"
                        />
                      </label>
                      <button
                        type="button"
                        className="btn-primary w-full"
                        onClick={() => acknowledge.mutate()}
                        disabled={acknowledge.isPending}
                      >
                        <Check className="h-4 w-4" aria-hidden />
                        Acknowledge
                      </button>
                    </>
                  ) : null}

                  {can('alert:resolve') ? (
                    <button
                      type="button"
                      className="btn-secondary w-full"
                      onClick={() => resolve.mutate()}
                      disabled={resolve.isPending}
                    >
                      <CheckCheck className="h-4 w-4" aria-hidden />
                      Mark resolved
                    </button>
                  ) : null}

                  {!can('alert:ack') && !can('alert:resolve') ? (
                    <p className="text-sm text-muted">Your role can view alerts but not act on them.</p>
                  ) : null}
                </>
              )}

              {actionError ? (
                <p className="rounded-lg border border-critical/40 bg-critical/10 px-3 py-2 text-sm text-critical">
                  {actionError}
                </p>
              ) : null}
            </div>
          </Panel>

          <Panel title="Reference">
            <dl className="space-y-2 p-4 text-xs">
              <Detail label="Alert id" value={alert.id} mono />
              <Detail label="Dedup key" value={`${alert.dedupKey.slice(0, 16)}…`} mono />
            </dl>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className={mono ? 'mt-0.5 break-all font-mono text-xs text-muted' : 'mt-0.5'}>{value}</dd>
    </div>
  );
}

function StatusChip({ status }: { status: Alert['status'] }) {
  const styles: Record<Alert['status'], string> = {
    NEW: 'border-high/40 bg-high/10 text-high',
    ACKNOWLEDGED: 'border-low/40 bg-low/10 text-low',
    RESOLVED: 'border-online/40 bg-online/10 text-online',
    // FAILED means every delivery attempt was exhausted — nobody was told. It
    // has to look different from "resolved", not merely grey.
    FAILED: 'border-critical/40 bg-critical/10 text-critical',
  };
  return (
    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase ${styles[status]}`}>
      {status === 'FAILED' ? 'Delivery failed' : status}
    </span>
  );
}

function DeliveryChip({ status }: { status: string }) {
  const tone =
    status === 'DELIVERED' || status === 'SENT'
      ? 'text-online'
      : status === 'FAILED'
        ? 'text-critical'
        : status === 'SKIPPED'
          ? 'text-muted'
          : 'text-high';
  return <span className={`text-xs font-semibold ${tone}`}>{status}</span>;
}
